import React, { useState, useEffect, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { range, pick, uniq, omit } from 'ramda';
import {
  Stack,
  Input,
  Select,
  CreditCardNumberInput,
  Button,
  Text,
  Loading,
  Checkbox,
  Overlay,
  LoadingBalls
} from '@moda/om';
import { useForm, YEARS_TO_MONTHS } from '@moda/portal-stanchions';
import { logger } from '../../../lib/logger';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { reportError } from '../../../lib/reportError';
import { getNetworkErrors, isGenericError } from '../../../lib/error';
import { useUser } from '../../../hooks/useUser';
import { usePaymentInfoEnteredTracking } from '../../../hooks/useCheckout';
import { useAddressesQuery, RestAddress } from '../../../hooks/useAddress';
import { useFormValidationTracking } from '../../../hooks/useCheckout/useFormValidationTracking';
import {
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  PaymentMethod
} from '../../../hooks/usePaymentMethods';
import { useAddressState } from '../../ShippingAddress/ShippingAddressForm/useAddressState';
import { getValidations as getAddressValidations } from '../../ShippingAddress/ShippingAddressForm/getValidations';
import { ShippingAddressFormFields } from '../../ShippingAddress';
import { usePaymentError } from '../../../hooks/useCheckout/usePaymentError';
import { CARD_VALIDATIONS } from './validations';
import { humanizeAddress } from './humanizeAddress';
import { createMaskWithLastFour } from './createMaskWithLastFour';
import { convertBillingAddressToShippingAddress } from './convertBillingAddressToShippingAddress';
import { convertShippingAddressToBillingAddress } from './convertShippingAddressToBillingAddress';

import './PaymentMethodForm.scss';

const TEN = 10;
const MAX_EXPIRATION_YEARS = 19;
const CURRENT_YEAR = new Date().getFullYear();

const ADDRESS_VALIDATION_FIELDS = ['addressLine1', 'addressLine2', 'city', 'state', 'postalCode'];

enum Mode {
  Resting,
  Saving
}

interface Props {
  method?: PaymentMethod;
  submitButtonLabel?: string;
  confirmCVV?: boolean;
  onCancel?: () => void;
  onSave: (method?: PaymentMethod) => void | Promise<void>;
}

type FormValues = {
  addressId: string;
  cardholderName: string;
  cardNumber: string;
  expirationMonth: string;
  expirationYear: string;
  securityCode: string;
};

export const PaymentMethodForm: React.FC<Props> = ({
  method,
  submitButtonLabel = 'Save',
  confirmCVV,
  onCancel,
  onSave
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isNew = !method;
  const billingAddress = method?.attributes.billingAddress;
  const { user } = useUser();
  const { paymentError, setPaymentError } = usePaymentError();

  const [mode, setMode] = useState(Mode.Resting);

  const [errorMessage, setErrorMessage] = useState('');

  const [useExistingAddress, setUseExistingAddress] = useState(
    isNew ? false : Boolean(billingAddress?.id)
  );

  const [shippingAddressAttributes, setShippingAddressAttributes] = useAddressState(
    isNew || billingAddress?.id ? undefined : convertBillingAddressToShippingAddress(billingAddress)
  );

  const { addresses, error, loading } = useAddressesQuery();

  const [createPaymentMethod] = useCreatePaymentMethodMutation();
  const [updatePaymentMethod] = useUpdatePaymentMethodMutation();
  const { trackPaymentInfoEntered } = usePaymentInfoEnteredTracking();
  const { trackFormValidation } = useFormValidationTracking();

  const onSubmit = useCallback(
    async (fields: FormValues) => {
      setMode(Mode.Saving);
      setErrorMessage('');
      if (paymentError) setPaymentError(null);
      try {
        const address = fields.addressId
          ? addresses.find(address => address.id === fields.addressId)
          : ({
              __typename: 'RestAddress',
              id: '',
              type: 'addresses',
              attributes: {
                __typename: 'RestAddressAttributes',
                verified: true,
                source: 'moda',
                firstName: '',
                lastName: '',
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: '',
                postalCode: '',
                phone: '',
                countryCode: '',
                ...fields
              }
            } as RestAddress);

        if (!address) throw new Error('PaymentMethodForm: address not found');

        const billingAddress = convertShippingAddressToBillingAddress(address);

        const commonArgs = {
          cardholderName: fields.cardholderName,
          expirationMonth:
            Number(fields.expirationMonth) < TEN
              ? `0${fields.expirationMonth}`
              : fields.expirationMonth,
          expirationYear: fields.expirationYear,
          securityCode: fields.securityCode,
          billingAddress
        };

        const result = method
          ? (
              await updatePaymentMethod({
                id: method.id,
                ...commonArgs
              })
            ).data?.updatePaymentMethod.data
          : (
              await createPaymentMethod({
                cardNumber: fields.cardNumber.replace(/\s/g, ''),
                ...commonArgs
              })
            ).data?.createPaymentMethod.data;

        await onSave(result);
        trackPaymentInfoEntered(result);
      } catch (error) {
        if (!isGenericError(error)) return;

        const networkErrorMessage = getNetworkErrors(error)?.[0]?.detail;
        const message =
          networkErrorMessage || error.displayMessage || 'An error occurred. Please retry later.';

        setErrorMessage(message);
        reportError('PAYMENT_METHOD_FORM_SUBMIT', error);

        logger.info('PAYMENT_METHOD_FAILED', error.toString(), {
          error: error.toString(),
          networkErrorMessage,
          message,
          email: user.email,
          guest: !user.isLoggedIn
        });
      }
      ref.current && setMode(Mode.Resting);
    },
    [
      paymentError,
      setPaymentError,
      addresses,
      method,
      updatePaymentMethod,
      createPaymentMethod,
      onSave,
      trackPaymentInfoEntered,
      user
    ]
  );

  const cardValidations = isNew ? CARD_VALIDATIONS : omit(['cardNumber'], CARD_VALIDATIONS);

  const { handleSubmit, errors, hasErrors } = useForm({
    onSubmit,
    validations: useExistingAddress
      ? cardValidations
      : {
          ...cardValidations,
          ...pick(
            ADDRESS_VALIDATION_FIELDS as keyof typeof getAddressValidations,
            getAddressValidations(shippingAddressAttributes)
          )
        }
  });

  const [defaultExpirationMonth, defaultExpirationYear] = method?.attributes.expirationDate?.split(
    '/'
  ) || ['1', CURRENT_YEAR.toString()];

  useEffect(() => {
    addresses.length > 0 && isNew && setUseExistingAddress(true);
  }, [addresses, isNew, setUseExistingAddress]);

  useEffect(() => {
    hasErrors && trackFormValidation('Payment Method Form Validation Error', errors);
  }, [hasErrors, errors, trackFormValidation]);

  if (error) throwQueryError(error);

  return (
    <>
      <form
        className={classNames('PaymentMethodForm', { 'PaymentMethodForm--error': errorMessage })}
        onSubmit={handleSubmit}
      >
        <Stack space={4}>
          <div className="PaymentMethodForm__name-number" ref={ref}>
            <Input
              className="PaymentMethodForm__input"
              label="Card Holder's Name"
              name="cardholderName"
              error={errors.cardholderName?.message}
              disabled={!isNew}
              defaultValue={isNew ? '' : method?.attributes.cardholderName}
            />
            {isNew ? (
              <CreditCardNumberInput
                className="PaymentMethodForm__input"
                maskChar=""
                label="Card Number"
                name="cardNumber"
                error={errors.cardNumber?.message}
              />
            ) : (
              <Input
                className="PaymentMethodForm__input"
                label="Card Number"
                name="cardNumber"
                disabled
                defaultValue={createMaskWithLastFour(method)}
              />
            )}
          </div>
          <div className="PaymentMethodForm__expiration-cvv">
            <Stack className="PaymentMethodForm__expiration" direction="horizontal" space={4}>
              <Input
                className="PaymentMethodForm__input"
                label="Exp Month"
                name="expirationMonth"
                error={errors.expirationMonth?.message}
                value={isNew ? '' : defaultExpirationMonth}
              >
                <Select
                  defaultValue={defaultExpirationMonth}
                  label=""
                  options={uniq([
                    ...range(1, YEARS_TO_MONTHS + 1),
                    Number(defaultExpirationMonth)
                  ]).map((month: number) => ({
                    label: month < TEN ? `0${month}` : `${month}`,
                    value: month.toString()
                  }))}
                  idRef="expirationMonth"
                />
              </Input>
              <Input
                className="PaymentMethodForm__input"
                label="Exp Year"
                name="expirationYear"
                error={errors.expirationYear?.message}
                value={isNew ? '' : defaultExpirationYear}
              >
                <Select
                  defaultValue={defaultExpirationYear}
                  label=""
                  options={uniq([
                    ...range(CURRENT_YEAR, CURRENT_YEAR + MAX_EXPIRATION_YEARS),
                    Number(defaultExpirationYear)
                  ]).map((year: number) => ({
                    label: year.toString(),
                    value: year.toString()
                  }))}
                  idRef="expirationYear"
                />
              </Input>
            </Stack>
            <Input
              className="PaymentMethodForm__input"
              label="Security Code"
              name="securityCode"
              error={
                errors.securityCode?.message ||
                (confirmCVV ? 'Please enter your 3 or 4 digit CVV code.' : undefined)
              }
            />
          </div>
          <div className="PaymentMethodForm__divider" />
          {loading && <Loading />}
          {!loading && (
            <>
              <Text treatment="eyebrow">Billing Address</Text>
              {addresses.length > 0 && (
                <Checkbox
                  checked={useExistingAddress}
                  onChange={event => setUseExistingAddress(event.target.checked)}
                >
                  Use an existing address
                </Checkbox>
              )}
              {useExistingAddress && (
                <Select
                  name="addressId"
                  options={addresses.map(address => ({
                    label: humanizeAddress(address),
                    value: address.id
                  }))}
                  defaultValue={
                    billingAddress?.id &&
                    addresses.some(address => address.id === billingAddress.id)
                      ? billingAddress.id
                      : addresses[0]?.id
                  }
                />
              )}

              {!useExistingAddress && (
                <ShippingAddressFormFields
                  address={shippingAddressAttributes}
                  onChange={setShippingAddressAttributes}
                  version="compact"
                  mutationFieldErrors={undefined}
                  validationErrors={errors}
                />
              )}
            </>
          )}

          {errorMessage && (
            <Text treatment="body1" color="code-red">
              {errorMessage}
            </Text>
          )}

          <Stack space={2} direction="horizontal" justifyContent="flex-end">
            {onCancel && (
              <Button
                className="PaymentMethodForm__button"
                secondary
                type="button"
                onClick={onCancel}
                data-testid="PaymentMethodForm__button"
              >
                Cancel
              </Button>
            )}
            <Button className="PaymentMethodForm__button" type="submit">
              {submitButtonLabel}
            </Button>
          </Stack>
        </Stack>
      </form>

      <Overlay show={mode === Mode.Saving}>
        <LoadingBalls />

        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
